import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowClockwise, Gear, MagnifyingGlass, X } from 'shared/icons';
import OrderLogisticsBadge from '../../components/pedidos/OrderLogisticsBadge';
import {
    FormatUrgencyCutoff,
    ManualTagChip,
    RowAccentClass,
    SortTagsActionFirst,
    TagChip,
    UrgentRuleTitle,
} from '../../components/pedidos/pedidosShared';
import { fmtDate } from '../../components/rma/rmaShared';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useToast } from '../../components/ui/Toast';
import { ML_LOGISTIC_TYPE_LABELS, ML_SHIPPING_STATUS_LABELS, MlLabel } from '../../constants/mlClaimCatalog';
import {
    FINANCE_REVIEW_FILTERS,
    FINANCE_REVIEW_META,
    FINANCE_REVIEW_STATUSES,
    INSPECTION_STATION_SECTOR,
    IsKnownTagKey,
    ManualTagColor,
    ORDER_STATUS_FILTERS,
    SECTOR_META,
    SECTOR_ORDER,
    SlaMinutesByTag,
    TAG_KIND_META,
    TAG_KINDS,
    TagKeysBySector,
    TONE_CLASSES,
} from '../../constants/pedidoTags';
import { useAuth } from '../../hooks/useAuth';
import api from '../../services/api';
import { FormatInt, FormatMoney } from '../../utils/format';
import { FinanceReviewCell } from './financeReview';
import {
    BlingOrderCell,
    ChannelCell,
    FilterChip,
    FLAT_FIELD_CLASSES,
    InspectionTakeover,
    LocalTagLabel,
    OrderIdentityCell,
    PackCell,
    PagerBar,
    PRIMARY_BUTTON_CLASSES,
    ResolveTagMeta,
    SECONDARY_BUTTON_CLASSES,
    SectorMenu,
    SyncAllControl,
    useSyncAll,
} from './listParts';

/**
 * Gestão de Pedidos — lista (`/pedidos`), §3.1 da spec (2026-09-22) + rodada 2
 * (2026-09-23: pack em coluna própria, chips com toggle único, setores por
 * permissão, identidade multi-canal com selo de origem nas tags) + rodada 3
 * (tabela no modelo multi-canal: Canal com a logo do marketplace, Pedido do
 * canal, Pack e Pedido Bling em colunas separadas; catálogo v3 de tags: chips
 * do setor em dois grupos — "Para fazer" (ação) e "Acompanhar" — e tags de
 * ação primeiro na célula; vermelho SÓ para `urgent` do wire) + rodada 3b
 * (Regras: urgente = ação com prazo do ML até o corte da Regra 1 OU com o
 * SLA interno estourado — blocos com urgentes em grande e "N para fazer · T
 * no total"; chip "Urgentes" (`?urgent=true`) antes de "Para fazer"; title
 * do chip de tag com o prazo/atraso; botão "Regras" no cabeçalho; lista em
 * largura total) + rodada 4 (processo de devolução: a Logística etiqueta, o
 * ATENDIMENTO inspeciona — o botão "▮ Iniciar inspeção" fica no bloco
 * Atendimento; recarga que falha limpa a tabela; correções automáticas da URL
 * com `replace`; "abrir caso" da estação só vai à tela nova com a venda) +
 * rodada 5 (blocos: o número grande volta a ser o de PENDÊNCIAS — "para
 * fazer" — com "N urgentes" em vermelho ao lado; Financeiro com
 * "Conciliar/verificar caso" para todo caso encerrado) + rodada 8
 * ("Sincronizar tudo" no cabeçalho com "Última sincronização"; destino da
 * devolução inspecionada definido pelo supervisor — tag
 * `devolucao_para_definir_destino`, que o backend só manda a quem tem
 * `rmas:resolve` — e o destino no chip "Devolução para destinar: …") +
 * rodada 5 de 2026-09-29 (coluna "Financeiro" com o status do financeiro do
 * pedido — Não visto / Visto / Pendência — trocável ali mesmo por quem tem
 * `rmas:finance:update`, e o filtro `?finance_review=` na barra de contexto;
 * só com o setor financeiro visível: o wire manda `finance_review: null`
 * quando não é).
 *
 * Uma linha por pedido, pendências como TAGS calculadas pelo backend e
 * agrupadas em três setores — Atendimento, Logística, Financeiro — dos quais
 * só aparecem os que o usuário pode ver (`summary.visible_sectors`). Os
 * filtros vivem na URL (F5 mantém o estado); o summary só é refeito quando
 * muda o CONTEXTO (busca, período, status, tag manual) — trocar de setor/tag/
 * página refaz só a lista. Respostas obsoletas são descartadas por sequência.
 */

const PAGE_SIZE = 50;
// `prazo` (2026-10-07): prazo do ML mais próximo — entra sozinho ao filtrar por
// uma tag com prazo (reclamação/mediação/recontato para responder…).
const ORDER_BY_OPTIONS = ['date_created', 'total_amount', 'prazo'];
// Mesmo teto do backend (`q` ≤ 120 em routes/pedidos.js) — F15.
const SEARCH_MAX_LENGTH = 120;
// Colunas (rodada 3): Canal | Pedido do canal | Pack | Pedido Bling | Data |
// Comprador | Produtos | Valor | Envio | Tags. Fixas somam ~52rem + mínimos
// de Produtos/Tags (25rem) + gaps/padding → a linha nunca fica abaixo de
// 86rem (scroll horizontal em telas menores). A sobra vai primeiro para Tags
// (até 20rem — os rótulos do catálogo v3 são longos) e depois para Produtos.
const GRID_COLUMNS = 'grid-cols-[2.75rem_9rem_7.5rem_6rem_7.5rem_7rem_minmax(12rem,1fr)_6rem_7rem_minmax(13rem,20rem)]';
const ROW_MIN_WIDTH = 'min-w-[86rem]';
// Com a coluna "Financeiro" (rodada 5, 2026-09-29): +7rem entre Envio e Tags.
const GRID_COLUMNS_FINANCE =
    'grid-cols-[2.75rem_9rem_7.5rem_6rem_7.5rem_7rem_minmax(12rem,1fr)_6rem_7rem_7rem_minmax(13rem,20rem)]';
const ROW_MIN_WIDTH_FINANCE = 'min-w-[93rem]';
const NO_ROWS = [];
const NO_SLA = {};
// Processo de devolução (rodada 4, decisão do Thiago) — rodapé dos blocos.
const RETURN_PROCESS_NOTES = {
    atendimento:
        ' O Atendimento acompanha o caso e dá o veredito ao ML. As devoluções físicas ficam nas filas de retirada e triagem da Logística.',
    logistica:
        ' Devoluções: retire nos Correios quando necessário; ao chegar ao galpão, receba, etiquete e acompanhe na fila de triagem. Após a inspeção, o supervisor define o destino.',
};

function parseCsv(value) {
    return String(value || '')
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
}

/**
 * Filtros lidos da URL — a URL é a fonte de verdade; valor inválido cai no
 * padrão. As tags (automática e manual) são de seleção ÚNICA (decisão 3):
 * a URL guarda no máximo uma. Key de tag fora do catálogo do front
 * (`PEDIDO_TAG_META` — ex.: URL salva com uma key v2) é IGNORADA na leitura:
 * o backend recusa key desconhecida com 400 e a tela abriria quebrada.
 * `urgent`/`acao` (Regras, rodada 3b) = filtros "Urgentes" e "Para fazer".
 * `finance_review` (rodada 5) = status do financeiro, multi (OU), na ordem
 * canônica; valor desconhecido cai fora.
 */
function readFilters(searchParams) {
    const sector = searchParams.get('sector') || '';
    const orderBy = searchParams.get('order_by') || '';
    const pageRaw = Number.parseInt(searchParams.get('page') || '1', 10);
    return {
        q: searchParams.get('q') || '',
        date_from: searchParams.get('date_from') || '',
        date_to: searchParams.get('date_to') || '',
        status: searchParams.get('status') || '',
        manual_tags: parseCsv(searchParams.get('manual_tags'))
            .filter((id) => /^\d+$/.test(id))
            .slice(0, 1),
        sector: SECTOR_ORDER.includes(sector) ? sector : '',
        tags: parseCsv(searchParams.get('tags')).filter(IsKnownTagKey).slice(0, 1),
        finance_review: FINANCE_REVIEW_FILTERS.filter((value) =>
            parseCsv(searchParams.get('finance_review')).includes(value),
        ),
        only_pending: searchParams.get('only_pending') === 'true',
        urgent: searchParams.get('urgent') === 'true',
        acao: searchParams.get('acao') === 'true',
        order_by: ORDER_BY_OPTIONS.includes(orderBy) ? orderBy : 'date_created',
        order_dir: searchParams.get('order_dir') === 'asc' ? 'asc' : 'desc',
        page: Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1,
    };
}

/** Só o que difere do padrão vai para a URL (mesmos nomes do wire). */
function writeFilters(filters) {
    const searchParams = new URLSearchParams();
    const set = (key, value) => {
        if (value !== undefined && value !== null && value !== '') {
            searchParams.set(key, String(value));
        }
    };
    set('q', filters.q);
    set('date_from', filters.date_from);
    set('date_to', filters.date_to);
    set('status', filters.status);
    set('manual_tags', filters.manual_tags.join(','));
    set('sector', filters.sector);
    set('tags', filters.tags.join(','));
    set('finance_review', filters.finance_review.join(','));
    if (filters.only_pending) {
        set('only_pending', 'true');
    }
    if (filters.urgent) {
        set('urgent', 'true');
    }
    if (filters.acao) {
        set('acao', 'true');
    }
    if (filters.order_by !== 'date_created') {
        set('order_by', filters.order_by);
    }
    if (filters.order_dir !== 'desc') {
        set('order_dir', filters.order_dir);
    }
    if (filters.page > 1) {
        set('page', filters.page);
    }
    return searchParams;
}

const HEADER_CELL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';
/** Rótulo de grupo na barra de chips ("Para fazer", "Acompanhar", "Tag manual"). */
const GROUP_LABEL = 'text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600';
const EMPTY_TAG_GROUPS = { acao: [], acompanhamento: [], all: [] };
/** Estado "nenhum chip selecionado" da barra de chips (seleção única). */
const NO_CHIP_FILTERS = { tags: [], only_pending: false, urgent: false, acao: false };
// A fila principal reúne as fases físicas. Preserva filtros antigos quando
// abertos por URL e os indicadores de fase na linha de cada pedido.
const TRIAGE_SUBSTEP_TAGS = new Set(['devolucao_para_etiquetar', 'devolucao_para_inspecionar']);

/** Cabeçalho ordenável (Data, Valor): clique alterna a coluna e a direção. */
const SortHeader = ({ column, label, orderBy, orderDir, onSort, align = 'left' }) => {
    const active = orderBy === column;
    return (
        <button
            type="button"
            onClick={() => onSort(column)}
            title={
                active
                    ? `Ordenado por ${label} (${orderDir === 'asc' ? 'crescente' : 'decrescente'})`
                    : `Ordenar por ${label}`
            }
            className={`${HEADER_CELL} inline-flex items-center gap-1 transition-colors hover:text-black dark:hover:text-white ${
                align === 'right' ? 'justify-end text-right' : ''
            } ${active ? 'text-black dark:text-white' : ''}`}
        >
            {label}
            <span className="text-[9px]">{active ? (orderDir === 'asc' ? '▲' : '▼') : ''}</span>
        </button>
    );
};

/** Célula "Produtos": sku · título do primeiro item; "+N" quando há mais. */
const ItemsCell = ({ items }) => {
    const list = Array.isArray(items) ? items : [];
    if (!list.length) {
        return <span className="text-xs text-mono-300 dark:text-mono-700">—</span>;
    }
    const [first, ...rest] = list;
    const title = first.title || first.item_id || '—';
    return (
        <span className="flex min-w-0 items-center gap-2">
            <span className="min-w-0 truncate text-sm text-mono-800 dark:text-mono-200" title={title}>
                {first.sku ? (
                    <span className="mr-1.5 font-mono text-[11px] text-mono-500 dark:text-mono-400">{first.sku}</span>
                ) : null}
                {title}
                {Number(first.quantity) > 1 ? (
                    <span className="ml-1 text-[11px] text-mono-400 dark:text-mono-500">×{first.quantity}</span>
                ) : null}
            </span>
            {rest.length > 0 ? (
                <span
                    className="shrink-0 bg-mono-100 px-1.5 py-0.5 text-[11px] text-mono-500 dark:bg-mono-900 dark:text-mono-400"
                    title={rest.map((item) => [item.sku, item.title].filter(Boolean).join(' · ')).join('\n')}
                >
                    +{rest.length}
                </span>
            ) : null}
        </span>
    );
};

/** Célula "Envio": status do envio (rótulo ML) e, embaixo, logística · UF. */
const ShippingCell = ({ shipping }) => {
    const status = MlLabel(ML_SHIPPING_STATUS_LABELS, shipping?.status);
    const logistic = MlLabel(ML_LOGISTIC_TYPE_LABELS, shipping?.logistic_type, '');
    const place = [logistic, shipping?.uf].filter(Boolean).join(' · ');
    return (
        <span className="flex min-w-0 flex-col leading-tight">
            <OrderLogisticsBadge logisticType={shipping?.logistic_type} />
            <span
                className={`truncate text-xs ${
                    status === '—' ? 'text-mono-300 dark:text-mono-700' : 'text-mono-700 dark:text-mono-200'
                }`}
            >
                {status}
            </span>
            {place ? <span className="truncate text-[11px] text-mono-400 dark:text-mono-500">{place}</span> : null}
        </span>
    );
};

/**
 * Linha: Canal (logo do marketplace) | Pedido do canal (nº em negrito mono) |
 * Pack | Pedido Bling ("—" até a integração) | Data | Comprador | Produtos |
 * Valor | Envio | Tags (automáticas — as de AÇÃO primeiro — e manuais, cada
 * uma com o selo de origem quando o wire trouxer `origin`). Borda esquerda
 * red só com ≥1 tag `urgent` do wire. A rota continua por `external_order_id`
 * (mantido no wire por compatibilidade).
 */
const PedidoRow = ({
    row,
    from,
    slaByTag = NO_SLA,
    financeColumn = false,
    canFinanceReview = false,
    onFinanceReviewSaved = null,
}) => {
    const tags = SortTagsActionFirst(Array.isArray(row.tags) ? row.tags : []);
    const manualTags = Array.isArray(row.manual_tags) ? row.manual_tags : [];
    return (
        <Link
            to={`/pedidos/${encodeURIComponent(row.external_order_id)}`}
            state={{ from }}
            className={`grid ${financeColumn ? GRID_COLUMNS_FINANCE : GRID_COLUMNS} ${
                financeColumn ? ROW_MIN_WIDTH_FINANCE : ROW_MIN_WIDTH
            } items-center gap-x-3 border-b border-l-4 border-b-mono-100 bg-white px-3 py-2 transition-colors hover:bg-mono-50 dark:border-b-mono-900 dark:bg-mono-950 dark:hover:bg-mono-900/60 ${RowAccentClass(
                tags,
            )}`}
        >
            <ChannelCell channel={row.channel} />
            <OrderIdentityCell orderId={row.channel_order_id} />
            <PackCell packId={row.pack_id} />
            <BlingOrderCell blingOrderId={row.bling_order_number || row.bling_order_id} />
            <span className="text-xs tabular-nums text-mono-600 dark:text-mono-300">{fmtDate(row.date_created)}</span>
            <span className="truncate text-sm text-mono-800 dark:text-mono-200" title={row.buyer?.name || undefined}>
                {row.buyer?.nickname || row.buyer?.name || '—'}
            </span>
            <ItemsCell items={row.items} />
            <span className="text-right text-sm tabular-nums text-mono-800 dark:text-mono-200">
                {FormatMoney(row.totals?.total_amount)}
            </span>
            <ShippingCell shipping={row.shipping} />
            {financeColumn ? (
                <FinanceReviewCell
                    orderId={row.external_order_id}
                    review={row.finance_review}
                    canEdit={canFinanceReview}
                    onSaved={(review) => onFinanceReviewSaved?.(row.external_order_id, review)}
                />
            ) : null}
            <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
                {tags.map((tag) => (
                    <TagChip key={tag.key} tag={tag} slaMinutes={slaByTag[tag.key]} />
                ))}
                {manualTags.map((tag) => (
                    <ManualTagChip key={`m-${tag.id}`} tag={tag} />
                ))}
                {!tags.length && !manualTags.length ? (
                    <span className="text-[11px] text-mono-300 dark:text-mono-700">—</span>
                ) : null}
            </span>
        </Link>
    );
};

const Pedidos = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const location = useLocation();
    const { checkPermission } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = useMemo(() => readFilters(searchParams), [searchParams]);

    const [summary, setSummary] = useState(null);
    const [list, setList] = useState(null);
    const [listLoading, setListLoading] = useState(true);
    const [listError, setListError] = useState(null);
    const [definitions, setDefinitions] = useState([]);
    const [qInput, setQInput] = useState(filters.q);
    const [stationOpen, setStationOpen] = useState(false);
    // SLA por tag (Regras) — só para o title do chip ("SLA de 15 min").
    const [slaByTag, setSlaByTag] = useState(NO_SLA);
    // O wire já trouxe `finance_review` (objeto) em alguma linha: o backend
    // conhece o status do financeiro e o setor é visível — filtro liberado.
    const [financeWire, setFinanceWire] = useState(false);

    // Guardas contra resposta obsoleta: cada carga recebe um número de
    // sequência e só a ÚLTIMA pode escrever no estado.
    const summarySeq = useRef(0);
    const listSeq = useRef(0);
    const toastRef = useRef(toast);
    toastRef.current = toast;
    // Summary mais recente para o catch da lista (setores já confirmados).
    const summaryRef = useRef(null);
    summaryRef.current = summary;
    // Um erro de contexto (ex.: busca genérica demais) derruba summary E lista
    // com a mesma mensagem — um toast só.
    const lastToastRef = useRef({ message: '', at: 0 });

    useEffect(() => {
        setQInput(filters.q);
    }, [filters.q]);

    // Contexto (dirige o summary) × lista (contexto + setor/tag/página/ordem).
    const contextKey = JSON.stringify({
        q: filters.q || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        status: filters.status || undefined,
        manual_tags: filters.manual_tags,
    });
    // Ordem por prazo só vale com a tag filtrada; sem ela (limpar tag, troca de
    // bloco) a lista volta para a data, mais recente primeiro.
    const dueOrder = filters.order_by === 'prazo' && filters.tags.length > 0;
    const listOrderBy = dueOrder ? 'prazo' : filters.order_by === 'prazo' ? 'date_created' : filters.order_by;
    const listOrderDir = filters.order_by === 'prazo' && !dueOrder ? 'desc' : filters.order_dir;
    const listKey = JSON.stringify({
        ...JSON.parse(contextKey),
        sector: filters.sector || undefined,
        tags: filters.tags,
        finance_review: filters.finance_review.length ? filters.finance_review.join(',') : undefined,
        only_pending: filters.only_pending ? 'true' : undefined,
        urgent: filters.urgent ? 'true' : undefined,
        acao: filters.acao ? 'true' : undefined,
        order_by: listOrderBy,
        order_dir: listOrderDir,
        limit: PAGE_SIZE,
        offset: (filters.page - 1) * PAGE_SIZE,
    });

    const reportError = useCallback((err, fallback) => {
        const message = err?.message || fallback;
        const now = Date.now();
        if (lastToastRef.current.message === message && now - lastToastRef.current.at < 2000) {
            return;
        }
        lastToastRef.current = { message, at: now };
        toastRef.current.error(message);
    }, []);

    const loadSummary = useCallback(
        async (key) => {
            summarySeq.current += 1;
            const seq = summarySeq.current;
            try {
                const data = await api.getPedidosSummary(JSON.parse(key));
                if (seq === summarySeq.current) {
                    setSummary(data || null);
                }
            } catch (err) {
                if (seq === summarySeq.current) {
                    reportError(err, 'Não foi possível carregar os totais.');
                }
            }
        },
        [reportError],
    );

    // A lista guarda a CHAVE da consulta que a gerou: quem lê (clamp de
    // página) compara com a chave atual e ignora uma lista de outra consulta.
    const loadList = useCallback(
        async (key) => {
            listSeq.current += 1;
            const seq = listSeq.current;
            const params = JSON.parse(key);
            setListLoading(true);
            try {
                const data = await api.listPedidos(params);
                if (seq !== listSeq.current) {
                    return;
                }
                const results = Array.isArray(data?.results) ? data.results : [];
                setList({
                    key,
                    results,
                    paging: data?.paging || { total: 0, limit: PAGE_SIZE, offset: params.offset || 0 },
                });
                setListError(null);
                if (results.some((row) => row?.finance_review && typeof row.finance_review === 'object')) {
                    setFinanceWire(true);
                }
            } catch (err) {
                if (seq !== listSeq.current) {
                    return;
                }
                // F15: a tabela da consulta ANTERIOR não fica na tela com os
                // filtros novos — sem lista, o ramo de erro mostra a mensagem
                // e "Tentar novamente".
                setList(null);
                setListError(err?.message || 'Não foi possível carregar os pedidos.');
                // F16: setor da URL ainda não confirmado pelo summary (link
                // antigo/sem permissão) → 403 esperado; a correção automática
                // volta para Todos assim que o summary chegar — sem toast.
                const visible = summaryRef.current?.visible_sectors;
                const unconfirmedSector =
                    err?.status === 403 &&
                    params.sector &&
                    !(Array.isArray(visible) && visible.includes(params.sector));
                // Rodada 8: idem para TAG que o usuário não vê (ex.:
                // `devolucao_para_definir_destino` sem rmas:resolve → 403;
                // backend que ainda não conhece a key → 400) — o summary não
                // a traz e a correção automática limpa o filtro.
                const knownTags = summaryRef.current?.tags;
                const unconfirmedTag =
                    (err?.status === 403 || err?.status === 400) &&
                    Array.isArray(params.tags) &&
                    params.tags.length > 0 &&
                    !(knownTags && params.tags.every((key) => Object.hasOwn(knownTags, key)));
                // Rodada 5: filtro do status do financeiro sem o setor
                // financeiro visível → 403 esperado; a correção automática limpa.
                const unconfirmedFinance =
                    err?.status === 403 &&
                    Boolean(params.finance_review) &&
                    !(Array.isArray(visible) && visible.includes('financeiro'));
                if (!unconfirmedSector && !unconfirmedTag && !unconfirmedFinance) {
                    reportError(err, 'Não foi possível carregar os pedidos.');
                }
            } finally {
                if (seq === listSeq.current) {
                    setListLoading(false);
                }
            }
        },
        [reportError],
    );

    // Na primeira carga os dois disparam juntos (paralelo); depois cada um
    // só refaz quando a SUA chave muda.
    useEffect(() => {
        loadSummary(contextKey);
    }, [contextKey, loadSummary]);

    useEffect(() => {
        loadList(listKey);
    }, [listKey, loadList]);

    useEffect(() => {
        let alive = true;
        api.listPedidoTagDefinitions()
            .then((data) => {
                if (alive) {
                    setDefinitions(Array.isArray(data) ? data : []);
                }
            })
            .catch(() => {
                // Sem definições os chips caem nas tags manuais do summary (com contagem).
            });
        return () => {
            alive = false;
        };
    }, []);

    useEffect(() => {
        let alive = true;
        api.getPedidoRegras()
            .then((data) => {
                if (alive) {
                    setSlaByTag(SlaMinutesByTag(data?.rules));
                }
            })
            .catch(() => {
                // Sem regras o chip diz só "(SLA)" — nada quebra.
            });
        return () => {
            alive = false;
        };
    }, []);

    const refresh = useCallback(() => {
        loadSummary(contextKey);
        loadList(listKey);
    }, [contextKey, listKey, loadSummary, loadList]);

    // "Sincronizar tudo" (dono, 2026-09-23): ao terminar, recarrega summary +
    // lista. Disparar exige orders:write; o status (orders:read) todos leem.
    const syncAll = useSyncAll({ toast, onFinished: refresh });
    const canSync = checkPermission('orders:write');

    // Toda mudança de filtro volta à página 1 (offset 0), salvo quando o patch
    // traz a página (paginador, clamp). `opts` vai para o `setSearchParams`
    // (F16: as correções AUTOMÁTICAS usam `{ replace: true }` — não empilham
    // no histórico e o Voltar do navegador sai da tela normalmente).
    const applyFilters = useCallback(
        (patch, opts = undefined) => {
            setSearchParams(writeFilters({ ...filters, page: 1, ...patch }), opts);
        },
        [filters, setSearchParams],
    );

    // Setores visíveis (decisão 8): o summary diz quais o usuário pode ver;
    // sem o campo, todos. Setor ativo invisível (URL antiga, permissão
    // retirada) volta para Todos — o backend responderia 403.
    const visibleSectors = useMemo(() => {
        if (!Array.isArray(summary?.visible_sectors)) {
            return SECTOR_ORDER;
        }
        return SECTOR_ORDER.filter((sector) => summary.visible_sectors.includes(sector));
    }, [summary]);
    useEffect(() => {
        if (summary && filters.sector && !visibleSectors.includes(filters.sector)) {
            applyFilters({ sector: '', ...NO_CHIP_FILTERS }, { replace: true });
        }
    }, [summary, visibleSectors, filters.sector, applyFilters]);
    // Tag da URL que o summary não traz = o backend não a mostra a este
    // usuário (rodada 8: `requires`, ex.: definir destino sem rmas:resolve)
    // → sai do filtro, como o setor invisível acima.
    useEffect(() => {
        const key = filters.tags[0];
        if (key && summary?.tags && !Object.hasOwn(summary.tags, key)) {
            applyFilters({ tags: [] }, { replace: true });
        }
    }, [summary, filters.tags, applyFilters]);
    // Status do financeiro sem o setor financeiro visível → sai do filtro.
    useEffect(() => {
        if (summary && filters.finance_review.length && !visibleSectors.includes('financeiro')) {
            applyFilters({ finance_review: [] }, { replace: true });
        }
    }, [summary, visibleSectors, filters.finance_review, applyFilters]);

    // Página além do total (F5 com `?page` velho, filtro que encolheu a lista):
    // cai na última página válida. Só age sobre a lista da consulta ATUAL.
    useEffect(() => {
        if (!list || list.key !== listKey) {
            return;
        }
        const lastPage = Math.max(1, Math.ceil((Number(list.paging?.total) || 0) / PAGE_SIZE));
        if (filters.page > lastPage) {
            applyFilters({ page: lastPage }, { replace: true });
        }
    }, [list, listKey, filters.page, applyFilters]);

    const changeSector = (sector) => {
        if (sector === filters.sector) {
            return;
        }
        // Os chips (tags, Urgentes, Para fazer, Com pendência) pertencem ao
        // bloco: trocar de bloco limpa todos.
        applyFilters({ sector, ...NO_CHIP_FILTERS });
    };
    // Toggle único (decisão 3) em TODA a barra de chips: clicar seleciona SÓ
    // aquele (tag, Urgentes, Para fazer ou Com pendência); de novo, limpa.
    // Tag com prazo do ML (o summary conta o "vence hoje" dela): a lista vem
    // pelo prazo, o mais próximo primeiro; ao sair dela, volta para a data.
    const toggleTag = (key) => {
        const selecting = filters.tags[0] !== key;
        const hasDue = Object.hasOwn(summary?.tags_due_today || {}, key);
        const order =
            selecting && hasDue
                ? { order_by: 'prazo', order_dir: 'asc' }
                : filters.order_by === 'prazo'
                  ? { order_by: 'date_created', order_dir: 'desc' }
                  : {};
        applyFilters({ ...NO_CHIP_FILTERS, tags: selecting ? [key] : [], ...order });
    };
    const toggleChipFlag = (flag) => {
        applyFilters({ ...NO_CHIP_FILTERS, [flag]: !filters[flag] });
    };
    // Status do financeiro (rodada 5): multi — cada chip entra/sai do conjunto.
    const toggleFinanceReview = (value) => {
        const set = new Set(filters.finance_review);
        if (set.has(value)) {
            set.delete(value);
        } else {
            set.add(value);
        }
        applyFilters({ finance_review: FINANCE_REVIEW_FILTERS.filter((item) => set.has(item)) });
    };
    // Troca feita no chip da linha: atualiza a linha na hora (sem refazer a
    // lista — a linha não some do filtro ativo) e refaz os totais dos blocos.
    const onFinanceReviewSaved = (orderId, review) => {
        setList((current) =>
            current
                ? {
                      ...current,
                      results: current.results.map((row) =>
                          row.external_order_id === orderId ? { ...row, finance_review: review } : row,
                      ),
                  }
                : current,
        );
        loadSummary(contextKey);
    };
    const toggleManualTag = (id) => {
        const value = String(id);
        applyFilters({ manual_tags: filters.manual_tags[0] === value ? [] : [value] });
    };
    const changeSort = (column) => {
        if (filters.order_by === column) {
            applyFilters({ order_dir: filters.order_dir === 'asc' ? 'desc' : 'asc' });
        } else {
            applyFilters({ order_by: column, order_dir: 'desc' });
        }
    };
    const submitSearch = (e) => {
        e.preventDefault();
        applyFilters({ q: qInput.trim() });
    };
    const clearSearch = () => {
        setQInput('');
        if (filters.q) {
            applyFilters({ q: '' });
        }
    };
    const hasContextFilters = Boolean(
        filters.q ||
            filters.date_from ||
            filters.date_to ||
            filters.status ||
            filters.manual_tags.length ||
            filters.finance_review.length,
    );
    const clearContextFilters = () => {
        setQInput('');
        applyFilters({ q: '', date_from: '', date_to: '', status: '', manual_tags: [], finance_review: [] });
    };

    // "Abrir caso" da estação: a inspeção conhece o RMA, não o pedido — resolve
    // o pedido e cai na tela nova; sem pedido (ocorrência manual), tela antiga.
    // `getRma` devolve o envelope `{ rma, sale, ... }` (RmaService.getRmaDetail).
    // F14: só vai à tela nova quando o envelope traz a VENDA — caso de pedido
    // não sincronizado cairia em "Pedido não encontrado"; fica na tela antiga.
    const openCaseFromStation = async (rmaId) => {
        setStationOpen(false);
        try {
            const detail = await api.getRma(rmaId);
            const orderId = detail?.sale ? detail?.rma?.external_order_id : null;
            if (orderId) {
                navigate(`/pedidos/${encodeURIComponent(orderId)}`);
                return;
            }
        } catch {
            // cai na tela antiga abaixo
        }
        navigate(`/rmas/${rmaId}`);
    };
    const closeStation = useCallback(() => setStationOpen(false), []);

    // Chips do setor ativo = SÓ as tags que o summary devolve (decisão 8),
    // na ordem do catálogo e depois as que o catálogo ainda não conhece,
    // divididas em dois grupos pelo `kind` (v3): "Para fazer" (ação) e
    // "Acompanhar". Setor e kind de uma key vêm do catálogo/fallback local ou,
    // em último caso, das linhas da página (`tags[].sector`/`tags[].kind` do
    // wire); key sem kind conhecido cai em "Acompanhar" (não se afirma ação
    // sem prova).
    const summaryTags = summary?.tags || null;
    const rows = list?.results || NO_ROWS;
    const sectorTagGroups = useMemo(() => {
        if (!filters.sector || !summaryTags) {
            return EMPTY_TAG_GROUPS;
        }
        const wire = new Map();
        for (const row of rows) {
            for (const tag of Array.isArray(row.tags) ? row.tags : []) {
                if (tag?.key && !wire.has(tag.key)) {
                    wire.set(tag.key, { sector: tag.sector || null, kind: tag.kind || null });
                }
            }
        }
        const inSector = Object.keys(summaryTags).filter(
            (key) =>
                (ResolveTagMeta(key).sector || wire.get(key)?.sector) === filters.sector &&
                (!TRIAGE_SUBSTEP_TAGS.has(key) || filters.tags.includes(key)),
        );
        const catalogFirst = TagKeysBySector(filters.sector).filter((key) => inSector.includes(key));
        const extras = inSector.filter((key) => !catalogFirst.includes(key));
        const all = [...catalogFirst, ...extras];
        const kindOf = (key) => ResolveTagMeta(key).kind || wire.get(key)?.kind || null;
        return {
            acao: all.filter((key) => kindOf(key) === 'acao'),
            acompanhamento: all.filter((key) => kindOf(key) !== 'acao'),
            all,
        };
    }, [filters.sector, filters.tags, summaryTags, rows]);
    const sectorTagKeys = sectorTagGroups.all;

    const manualCounts = new Map((summary?.manual_tags || []).map((tag) => [String(tag.id), Number(tag.count || 0)]));
    const manualOptions = definitions.length ? definitions : summary?.manual_tags || [];
    const manualSelected = filters.manual_tags[0] || '';
    const selectedTag = filters.tags[0] || '';
    const total = list?.paging?.total ?? null;
    const from = `${location.pathname}${location.search}`;
    // Coluna "Financeiro": só quando o wire traz o status (objeto) — `null` =
    // setor financeiro invisível; ausente = backend sem o campo.
    const financeColumn = rows.some((row) => row?.finance_review && typeof row.finance_review === 'object');
    const financeFilterVisible =
        visibleSectors.includes('financeiro') && (financeWire || filters.finance_review.length > 0);
    const canFinanceReview = checkPermission('rmas:finance:update');

    // Regra 1 (rodada 3b): "Urgentes" = ação nossa com prazo até o corte do
    // summary; conta do bloco ativo (setor) ou de Todos. Summary sem o campo
    // (backend antigo) → o chip some; sem summary ainda → "…".
    const urgentCount = summary
        ? filters.sector
            ? summary.sectors?.[filters.sector]?.urgent
            : summary.urgent_total
        : undefined;
    // F11: usuário sem NENHUM setor visível não tem pendência a contar nem a
    // filtrar — os chips de Todos (Urgentes, Para fazer, Com pendência) somem.
    const noVisibleSectors = Array.isArray(summary?.visible_sectors) && visibleSectors.length === 0;
    const showUrgentChip = !noVisibleSectors && (!summary || (urgentCount !== undefined && urgentCount !== null));
    const acaoCount = summary ? summary.acao_total : undefined;
    const showAcaoChip = !noVisibleSectors && (!summary || (acaoCount !== undefined && acaoCount !== null));
    const cutoffText = FormatUrgencyCutoff(summary?.urgency_cutoff);
    const urgentTitle = UrgentRuleTitle(summary?.urgency_cutoff);
    const urgentChip = showUrgentChip ? (
        <FilterChip
            active={filters.urgent}
            count={summary ? Number(urgentCount) || 0 : undefined}
            urgent={Number(urgentCount) > 0}
            title={urgentTitle}
            onClick={() => toggleChipFlag('urgent')}
        >
            Urgentes
        </FilterChip>
    ) : null;

    return (
        // Largura TOTAL (pedido do dono: "bordas gigantes nas laterais"): o
        // Layout já dá o teto e o respiro lateral — nada de max-w/mx-auto/p-*
        // aqui (mesmo container do detalhe).
        <div className="flex w-full flex-col gap-4">
            {/* ── Topo: rótulo, busca, atualizar ── */}
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Gestão de Pedidos
                </p>
                <form onSubmit={submitSearch} className="relative ml-auto">
                    <MagnifyingGlass
                        size={15}
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-mono-400"
                    />
                    <input
                        value={qInput}
                        onChange={(e) => setQInput(e.target.value)}
                        placeholder="Pedido, pack, rastreio, comprador ou SKU — Enter"
                        aria-label="Buscar pedido"
                        maxLength={SEARCH_MAX_LENGTH}
                        className={`${FLAT_FIELD_CLASSES} w-96 pl-9 pr-8`}
                    />
                    {qInput || filters.q ? (
                        <button
                            type="button"
                            onClick={clearSearch}
                            aria-label="Limpar busca"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-mono-400 transition-colors hover:text-black dark:hover:text-white"
                        >
                            <X size={13} />
                        </button>
                    ) : null}
                </form>
                <button
                    type="button"
                    onClick={refresh}
                    aria-label="Atualizar"
                    title="Atualizar a lista (relê o que já está sincronizado, sem ir ao Mercado Livre)"
                    className={SECONDARY_BUTTON_CLASSES}
                >
                    <ArrowClockwise size={15} />
                </button>
                <SyncAllControl sync={syncAll} canSync={canSync} />
                <Link
                    to="/pedidos/regras"
                    state={{ from }}
                    title="Regras: horário de atendimento, urgência e SLA de resposta"
                    className={SECONDARY_BUTTON_CLASSES}
                >
                    <Gear size={15} /> Regras
                </Link>
            </div>

            {/* ── Blocos: Todos | setores visíveis ── */}
            <SectorMenu summary={summary} sectors={visibleSectors} active={filters.sector} onChange={changeSector} />

            {/* ── Urgentes · tags do setor ativo em dois grupos (toggle único) · Para fazer/Com pendência em Todos · ação primária ── */}
            <div className="flex flex-wrap items-center gap-1.5">
                {urgentChip ? <span className="mr-3 flex items-center">{urgentChip}</span> : null}
                {filters.sector ? (
                    summaryTags ? (
                        TAG_KINDS.map((kind) =>
                            sectorTagGroups[kind].length ? (
                                <span key={kind} className="ml-3 flex flex-wrap items-center gap-1.5 first:ml-0">
                                    <span className={GROUP_LABEL} title={TAG_KIND_META[kind].title}>
                                        {TAG_KIND_META[kind].label}
                                    </span>
                                    {sectorTagGroups[kind].map((key) => {
                                        // `null` do backend = tag só de linha (sem contagem/filtro).
                                        const count = summaryTags[key];
                                        return (
                                            <FilterChip
                                                key={key}
                                                active={selectedTag === key}
                                                count={count}
                                                dueToday={summary?.tags_due_today?.[key]}
                                                action={kind === 'acao'}
                                                title={
                                                    Object.hasOwn(summary?.tags_due_today || {}, key)
                                                        ? `${TAG_KIND_META[kind].title} · "hoje" = prazo do ML até ${cutoffText || 'o corte da Regra 1'}; ao filtrar, a lista vem pelo prazo`
                                                        : TAG_KIND_META[kind].title
                                                }
                                                onClick={() => toggleTag(key)}
                                            >
                                                {ResolveTagMeta(key).label}
                                            </FilterChip>
                                        );
                                    })}
                                </span>
                            ) : null,
                        )
                    ) : (
                        <span className="text-[11px] text-mono-400 dark:text-mono-600">carregando tags…</span>
                    )
                ) : (
                    <>
                        {showAcaoChip ? (
                            <FilterChip
                                active={filters.acao}
                                count={summary ? Number(acaoCount) || 0 : undefined}
                                action
                                title={`${TAG_KIND_META.acao.title}: pedidos com pelo menos uma tag de ação`}
                                onClick={() => toggleChipFlag('acao')}
                            >
                                {TAG_KIND_META.acao.label}
                            </FilterChip>
                        ) : null}
                        {noVisibleSectors ? null : (
                            <FilterChip
                                active={filters.only_pending}
                                count={summary ? (summary.pending_total ?? 0) : undefined}
                                title="Pedidos com pelo menos uma tag (para fazer ou acompanhar) em qualquer setor"
                                onClick={() => toggleChipFlag('only_pending')}
                            >
                                Com pendência
                            </FilterChip>
                        )}
                    </>
                )}
                {selectedTag ? (
                    <button
                        type="button"
                        onClick={() => applyFilters({ tags: [] })}
                        className="px-1.5 text-[11px] text-mono-500 transition-colors hover:text-black dark:hover:text-white"
                    >
                        limpar tag{sectorTagKeys.includes(selectedTag) ? '' : ` (${LocalTagLabel(selectedTag)})`}
                    </button>
                ) : null}
                {filters.sector === INSPECTION_STATION_SECTOR ? (
                    <button
                        type="button"
                        onClick={() => setStationOpen(true)}
                        title="Inspecionar as devoluções que a Logística já etiquetou (estação em tela cheia)"
                        className={`${PRIMARY_BUTTON_CLASSES} ml-auto`}
                    >
                        ▮ Iniciar inspeção
                    </button>
                ) : null}
            </div>

            {/* ── Contexto: período, status, tag manual (dirigem o summary) ── */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border border-mono-200 bg-white px-3 py-2 dark:border-mono-800 dark:bg-mono-950">
                <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    De
                    <input
                        type="date"
                        value={filters.date_from}
                        max={filters.date_to || undefined}
                        onChange={(e) => applyFilters({ date_from: e.target.value })}
                        className={`${FLAT_FIELD_CLASSES} font-normal normal-case tracking-normal`}
                    />
                </label>
                <label className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-mono-400 dark:text-mono-600">
                    Até
                    <input
                        type="date"
                        value={filters.date_to}
                        min={filters.date_from || undefined}
                        onChange={(e) => applyFilters({ date_to: e.target.value })}
                        className={`${FLAT_FIELD_CLASSES} font-normal normal-case tracking-normal`}
                    />
                </label>
                <select
                    value={filters.status}
                    onChange={(e) => applyFilters({ status: e.target.value })}
                    aria-label="Status da venda"
                    className={FLAT_FIELD_CLASSES}
                >
                    {ORDER_STATUS_FILTERS.map((option) => (
                        <option key={option.value || 'all'} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
                {manualOptions.length ? (
                    <span className="flex flex-wrap items-center gap-1.5">
                        <span className={GROUP_LABEL}>Tag manual</span>
                        {manualOptions.map((tag) => {
                            const id = String(tag.id);
                            const count = manualCounts.get(id);
                            return (
                                <FilterChip
                                    key={id}
                                    active={manualSelected === id}
                                    count={summary ? (count ?? 0) : undefined}
                                    swatchClass={ManualTagColor(tag.color).swatch}
                                    onClick={() => toggleManualTag(id)}
                                >
                                    {tag.name}
                                </FilterChip>
                            );
                        })}
                    </span>
                ) : null}
                {financeFilterVisible ? (
                    <span className="flex flex-wrap items-center gap-1.5">
                        <span className={GROUP_LABEL} title="Status do financeiro do pedido (marcado pelo financeiro)">
                            Financeiro
                        </span>
                        {FINANCE_REVIEW_STATUSES.map((status) => {
                            const meta = FINANCE_REVIEW_META[status];
                            return (
                                <FilterChip
                                    key={status}
                                    active={filters.finance_review.includes(meta.filter)}
                                    showCount={false}
                                    swatchClass={TONE_CLASSES[meta.tone]?.dot}
                                    title={`Financeiro: ${meta.label} (pode marcar mais de um)`}
                                    onClick={() => toggleFinanceReview(meta.filter)}
                                >
                                    {meta.label}
                                </FilterChip>
                            );
                        })}
                    </span>
                ) : null}
                {hasContextFilters ? (
                    <button
                        type="button"
                        onClick={clearContextFilters}
                        className="px-1.5 text-[11px] text-mono-500 transition-colors hover:text-black dark:hover:text-white"
                    >
                        limpar filtros
                    </button>
                ) : null}
                <span className="ml-auto flex items-center gap-2 text-sm tabular-nums text-mono-400 dark:text-mono-500">
                    {list && listLoading ? <span className="text-[11px]">atualizando…</span> : null}
                    {total !== null ? `${FormatInt(total)} pedidos` : null}
                    {total !== null && dueOrder ? ' · por prazo do ML' : null}
                </span>
            </div>

            {/* ── Tabela plana ── */}
            {!list && listLoading ? (
                <div className="flex justify-center py-16">
                    <LoadingSpinner />
                </div>
            ) : !list && listError ? (
                <div className="border border-dashed border-danger-300 py-12 text-center dark:border-danger-700">
                    <p className="text-sm text-danger-700 dark:text-danger-300">{listError}</p>
                    <button type="button" onClick={refresh} className={`${SECONDARY_BUTTON_CLASSES} mt-3`}>
                        Tentar novamente
                    </button>
                </div>
            ) : rows.length === 0 ? (
                <div className="border border-dashed border-mono-300 py-14 text-center dark:border-mono-700">
                    <p className="text-sm text-mono-600 dark:text-mono-300">Nenhum pedido com esses filtros.</p>
                    {listError ? (
                        <p className="mt-1 text-xs text-danger-600 dark:text-danger-400">{listError}</p>
                    ) : null}
                </div>
            ) : (
                <div
                    className={`overflow-x-auto border border-mono-200 dark:border-mono-800 ${listLoading ? 'opacity-60' : ''}`}
                    aria-busy={listLoading}
                >
                    <div
                        className={`grid ${financeColumn ? GRID_COLUMNS_FINANCE : GRID_COLUMNS} ${
                            financeColumn ? ROW_MIN_WIDTH_FINANCE : ROW_MIN_WIDTH
                        } items-center gap-x-3 border-b border-l-4 border-b-mono-200 border-l-transparent bg-mono-50 px-3 py-1.5 dark:border-b-mono-800 dark:bg-mono-900`}
                    >
                        <span className={HEADER_CELL}>Canal</span>
                        <span className={HEADER_CELL}>Pedido do canal</span>
                        <span className={HEADER_CELL}>Pack</span>
                        <span className={HEADER_CELL}>Pedido Bling</span>
                        <SortHeader
                            column="date_created"
                            label="Data"
                            orderBy={filters.order_by}
                            orderDir={filters.order_dir}
                            onSort={changeSort}
                        />
                        <span className={HEADER_CELL}>Comprador</span>
                        <span className={HEADER_CELL}>Produtos</span>
                        <SortHeader
                            column="total_amount"
                            label="Valor"
                            orderBy={filters.order_by}
                            orderDir={filters.order_dir}
                            onSort={changeSort}
                            align="right"
                        />
                        <span className={HEADER_CELL}>Envio</span>
                        {financeColumn ? (
                            <span
                                className={HEADER_CELL}
                                title="Status do financeiro: Não visto / Visto / Pendência — independente de abrir o pedido"
                            >
                                Financeiro
                            </span>
                        ) : null}
                        <span className={HEADER_CELL}>Tags</span>
                    </div>
                    {rows.map((row) => (
                        <PedidoRow
                            key={row.external_order_id}
                            row={row}
                            from={from}
                            slaByTag={slaByTag}
                            financeColumn={financeColumn}
                            canFinanceReview={canFinanceReview}
                            onFinanceReviewSaved={onFinanceReviewSaved}
                        />
                    ))}
                </div>
            )}

            {list && ((list.paging?.total || 0) > PAGE_SIZE || filters.page > 1) ? (
                <PagerBar
                    page={filters.page}
                    pageSize={PAGE_SIZE}
                    total={list.paging?.total || 0}
                    busy={listLoading}
                    onPage={(page) => applyFilters({ page })}
                />
            ) : null}

            <p className="text-[11px] text-mono-400 dark:text-mono-600">
                {filters.sector
                    ? `Bloco ${SECTOR_META[filters.sector]?.label}: pedidos com ao menos uma tag do setor. `
                    : ''}
                Para fazer = a vez é nossa; Acompanhar = estado de outra parte; vermelho = urgente — ação nossa com
                prazo do ML até {cutoffText || 'o corte da Regra 1'} ou que passou do SLA sem resposta (
                <Link to="/pedidos/regras" state={{ from }} className="underline underline-offset-2">
                    ver Regras
                </Link>
                ).{filters.sector ? ' Chips apagados aparecem só na linha (sem contagem).' : ''}
                {RETURN_PROCESS_NOTES[filters.sector] || ''}
            </p>

            <InspectionTakeover
                open={stationOpen}
                onClose={closeStation}
                toast={toast}
                onOpenCase={openCaseFromStation}
                onChanged={refresh}
                canLogistics={checkPermission('rmas:logistics:update')}
                canInspect={checkPermission('rmas:mediation:update') || checkPermission('rmas:logistics:update')}
            />
        </div>
    );
};

export default Pedidos;
